import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useScreenSize } from '../utils/responsive';
import { loadJson } from '../utils/apiRequest';
import Card from '../components/ui/Card';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import API_BASE_URL from '../config';
import OnboardingWizard from '../components/OnboardingWizard';
import AlertBanner from '../components/AlertBanner';
import { datumKurz } from '../utils/datum';
import { aufTaste } from '../utils/tastaturBedienung';

export default function Dashboard() {
  const { token, user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [kpis, setKpis] = useState(null);
  const [dealStats, setDealStats] = useState(null);
  const [campaignStats, setCampaignStats] = useState([]);
  const [leads, setLeads] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingSecondary, setLoadingSecondary] = useState(true);
  //: Die aktiven Warnungen. Sie stehen ueber allem anderen — wer das
  //: Dashboard oeffnet, soll zuerst sehen, was klemmt (L-95).
  const [warnungen, setWarnungen] = useState([]);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const h = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

    // Chain A — KPIs: rendert die obere Kennzahlen-Reihe zuerst
    loadJson(`${API_BASE_URL}/api/dashboard/kpis`, { headers: h }, { context: 'Kennzahlen', emptyOn: [] })
      .then(data => { if (data) setKpis(data); })
      .finally(() => setLoadingKpis(false));

    // Chain B — Leads: nur 8 werden angezeigt.
    //
    // **Der Rueckfall auf `/api/usercards/` ist am 01.09.2026 entfernt worden
    // — er konnte nie ausloesen.** Hier stand: ist die Liste leer, hole
    // stattdessen die Kundenkarten. Die Tabelle `usercards` wird seit dem
    // Entfernen ihres Kopierschritts nie befuellt; die einzige Stelle, die
    // eine Zeile anlegt, ist `POST /api/usercards/`, und die ruft keine
    // Oberflaeche auf (L-105, L-106). Der Zweig holte also zuverlaessig eine
    // leere Liste — mit einer zweiten Anfrage und dem Anschein, es gaebe eine
    // zweite Datenquelle. Wer das liest, sucht den fehlenden Betrieb dort.
    fetch(`${API_BASE_URL}/api/leads/?limit=8`, { headers: h })
      .then(r => r.json())
      .catch(() => [])
      .then(leadsData => {
        const rows = Array.isArray(leadsData) ? leadsData : [];
        setLeads(rows.slice(0, 8));
      })
      .finally(() => setLoadingLeads(false));

    // Chain C — Sekundaerdaten: Audits, Deal-Stats, Kampagnen-Stats
    Promise.all([
      fetch(`${API_BASE_URL}/api/audit/recent`,    { headers: h }).then(r => r.json()).catch(() => []),
      fetch(`${API_BASE_URL}/api/deals/stats`,     { headers: h }).then(r => r.json()).catch(() => null),
      fetch(`${API_BASE_URL}/api/campaigns/stats`, { headers: h }).then(r => r.json()).catch(() => []),
      // Der Rueckfall auf `[]` ist hier kein Beiwerk: Diese Route antwortete
      // produktiv einmal mit 500 (L-53, `scope_creep_flags` war NULL). Ein
      // Dashboard, das daran zerbricht, waere schlimmer als eines ohne
      // Warnungen.
      fetch(`${API_BASE_URL}/api/dashboard/alerts`, { headers: h })
        .then(r => (r.ok ? r.json() : [])).catch(() => []),
    ]).then(([auditData, dealsData, campaignData, alertData]) => {
      setAudits(Array.isArray(auditData) ? auditData.slice(0, 5) : []);
      setDealStats(dealsData);
      setCampaignStats(Array.isArray(campaignData) ? campaignData : []);
      setWarnungen(Array.isArray(alertData) ? alertData : []);
    }).finally(() => setLoadingSecondary(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || onboardingChecked) return;
    setOnboardingChecked(true);

    // Nur für Kunden mit verknüpftem Lead
    if (user.role !== 'kunde' || !user.lead_id) return;

    // Onboarding-Status vom Server prüfen
    const checkOnboarding = async () => {
      const me = await loadJson(
        `${API_BASE_URL}/api/auth/me`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('kompagnon_token')}` } },
        { context: 'Kontodaten' }
      );
      if (me?.onboarding_completed === false) setShowOnboarding(true);
    };
    checkOnboarding();
  }, [user, onboardingChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  const KpiCard = ({ label, value, icon, delta, color }) => (
    <div style={{ background: 'var(--paper)', border: '0.5px solid var(--border)', borderRadius: 'var(--r-md)', padding: '16px 18px' }}>
      {loadingKpis ? (
        <Skeleton height={32} width={80} />
      ) : (
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: color || 'var(--brand-primary)', lineHeight: 1 }}>
          {value ?? '—'}
        </div>
      )}
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: 4, fontFamily: 'var(--font-sans)' }}>{label}</div>
      {delta !== undefined && !loadingKpis && (
        <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3, color: delta > 0 ? 'var(--success)' : 'var(--text-tertiary)', fontFamily: 'var(--font-sans)' }}>
          {delta > 0 ? '▲' : '▼'} {Math.abs(delta)} diese Woche
        </div>
      )}
    </div>
  );

  const avgScore = audits.length
    ? Math.round(audits.reduce((s, a) => s + (a.total_score || 0), 0) / audits.length)
    : null;

  const statusBadge = (status) => {
    const map = {
      new: ['neutral', 'Neu'],
      contacted: ['info', 'Kontaktiert'],
      qualified: ['success', 'Qualifiziert'],
      proposal_sent: ['warning', 'Angebot'],
      won: ['success', 'Gewonnen'],
      lost: ['danger', 'Verloren'],
    };
    const [v, l] = map[status] || ['neutral', status];
    return <Badge variant={v}>{l}</Badge>;
  };

  const scoreColor = (s) =>
    s >= 70 ? 'var(--status-success-text)'
    : s >= 50 ? 'var(--status-warning-text)'
    : 'var(--status-danger-text)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, animation: 'fadeIn 0.3s ease', width: '100%', minWidth: 0, overflowX: 'hidden' }}>

      {showOnboarding && (
        <OnboardingWizard
          user={user}
          onComplete={() => {
            setShowOnboarding(false);
            setOnboardingChecked(true);
          }}
        />
      )}

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '0.02em', lineHeight: 1, margin: 0 }}>
            Dashboard
          </h1>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: 4, fontFamily: 'var(--font-sans)' }}>
            {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
        <button
          onClick={() => navigate('/app/projektpipeline')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--kc-yellow)', color: '#000', border: 'none', borderRadius: 'var(--r-md)', fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          + Neuer Betrieb
        </button>
      </div>

      {/* Was klemmt, steht ueber den Zahlen. Der Scheduler rechnet diese
        * Warnungen seit jeher taeglich aus; angezeigt wurden sie nie, weil
        * `AlertBanner` von keiner Datei importiert war (L-95). */}
      <AlertBanner alerts={warnungen}
        onOeffnen={a => navigate(`/app/projects/${a.project_id}`)} />

      {/* Die Zahlen, die etwas sagen — und deshalb zuerst.
        * Vorher standen oben drei Kacheln mit 0,00 €, groß und grün, und
        * darunter klein die Zahlen, an denen man den Stand abliest. Die
        * Reihenfolge behauptete eine Wichtigkeit, die nicht stimmte (UX-11). */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12,
        minWidth: 0, width: '100%',
      }}>
        <KpiCard label="Betriebe gesamt" value={kpis?.leads_total ?? leads.length} icon="👥" />
        <KpiCard label="Audits heute" value={kpis?.audits_today ?? 0} icon="✓" color="var(--brand-primary)" />
        <KpiCard
          label="Ø Website-Score"
          value={avgScore !== null ? `${avgScore}/100` : '—'}
          icon="◎"
          color={avgScore ? scoreColor(avgScore) : undefined}
        />
        <KpiCard
          label="Gewonnene Betriebe"
          value={kpis?.leads_won ?? leads.filter(l => l.status === 'won').length}
          icon="🏆"
          color="var(--status-success-text)"
        />
      </div>

      {/* Deal-Metriken — darunter, weil sie meist 0,00 € zeigen.
        * Und die Null steht nicht mehr in Erfolgsgrün: Grün behauptet ein
        * Ergebnis, und 0,00 € ist keines. Farbe bekommt der Betrag erst,
        * wenn es etwas zu färben gibt. */}
      {loadingSecondary && !dealStats && (
        /* Platzhalter statt Leere: Die Reihe kam bisher ohne Ankündigung
          * dazu und schob alles darunter nach unten (UX-10). */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, minWidth: 0, width: '100%' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', padding: '18px 22px', border: '1px solid var(--border-light)' }}>
              <Skeleton height={12} width={110} />
              <div style={{ height: 8 }} />
              <Skeleton height={24} width={90} />
            </div>
          ))}
        </div>
      )}

      {dealStats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
          minWidth: 0, width: '100%',
        }}>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', padding: '18px 22px', border: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              💰 Heute gewonnen
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: dealStats.won_today > 0 ? 'var(--status-success-text)' : 'var(--text-secondary)' }}>
              {Number(dealStats.won_today).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
              {dealStats.deals_won_today} Deal{dealStats.deals_won_today !== 1 ? 's' : ''}
            </div>
          </div>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', padding: '18px 22px', border: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              📅 Diesen Monat
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: dealStats.won_this_month > 0 ? 'var(--status-success-text)' : 'var(--text-secondary)' }}>
              {Number(dealStats.won_this_month).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
          </div>
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', padding: '18px 22px', border: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              💼 Pipeline offen
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: dealStats.pipeline_value > 0 ? 'var(--brand-primary)' : 'var(--text-secondary)' }}>
              {Number(dealStats.pipeline_value).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
              {dealStats.deals_open} offene Deal{dealStats.deals_open !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}
      {/* Betriebe nach Herkunft */}
      {campaignStats.length > 0 && (() => {
        const SRC = {
          facebook:   { icon: '📘', label: 'Facebook' },
          linkedin:   { icon: '💼', label: 'LinkedIn' },
          google_ads: { icon: '🔍', label: 'Google Ads' },
          briefkarte: { icon: '📬', label: 'Briefkarte' },
          instagram:  { icon: '📸', label: 'Instagram' },
          email:      { icon: '✉️', label: 'E-Mail' },
          postkarte:  { icon: '📬', label: 'Postkarte' },
          sonstige:   { icon: '📌', label: 'Sonstige' },
          direkt:     { icon: '🔗', label: 'Direkt' },
        };
        const maxCount = Math.max(...campaignStats.map(s => s.lead_count || 0), 1);
        return (
          <div style={{
            background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
            padding: '18px 22px', border: '1px solid var(--border-light)',
            width: '100%', boxSizing: 'border-box',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: 'var(--text-primary)' }}>
              📊 Betriebe nach Herkunft
            </div>

            {/* Die rechte Zahl stand ohne Überschrift da und sah dadurch aus
              * wie ein Anteil an allem. Sie ist die Gewinnquote dieser
              * Herkunft — richtig gerechnet, nur unbenannt (UX-09). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ width: 24, flexShrink: 0 }} />
              <span style={{ width: 100, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 50 }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', minWidth: 70, textAlign: 'right', flexShrink: 0 }}>
                Betriebe
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', minWidth: 64, textAlign: 'right', flexShrink: 0 }}>
                Gewinnquote
              </span>
            </div>

            {campaignStats.map(stat => {
              const cfg = SRC[stat.source] || { icon: stat.source_icon || '📌', label: stat.source_label || stat.source };
              const cnt = stat.lead_count || 0;
              const won = stat.won_count || 0;
              const pct = cnt > 0 ? Math.round((won / cnt) * 100) : 0;
              const widthPct = Math.round((cnt / maxCount) * 100);
              return (
                <div key={stat.source} style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                }}>
                  <span style={{ fontSize: 16, width: 24, flexShrink: 0 }}>{cfg.icon}</span>
                  <span style={{ fontSize: 12, width: 100, color: 'var(--text-secondary)', flexShrink: 0 }}>{cfg.label}</span>
                  <div style={{ flex: 1, height: 8, background: 'var(--bg-app)', borderRadius: 4, overflow: 'hidden', minWidth: 50 }}>
                    <div style={{
                      width: `${widthPct}%`, height: '100%',
                      background: 'var(--brand-primary)',
                      borderRadius: 4, transition: 'width 0.5s ease',
                    }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)', minWidth: 70, textAlign: 'right', flexShrink: 0 }}>
                    {cnt}
                  </span>
                  {/* Das Häkchen stand für die fehlende Überschrift ein. Jetzt
                    * gibt es die Überschrift, also kann es weg. */}
                  <span style={{
                    fontSize: 12,
                    color: pct > 30 ? 'var(--status-success-text)' : 'var(--text-tertiary)',
                    minWidth: 64, textAlign: 'right', flexShrink: 0, fontWeight: 600,
                  }}>
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Zwei-Spalten */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.6fr) minmax(0, 1fr)',
        gap: 16,
        minWidth: 0, width: '100%', overflowX: 'hidden',
      }}>

        {/* Leads */}
        <Card padding="sm" style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 12, borderBottom: '1px solid var(--border-light)', marginBottom: 4 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              Aktuelle Betriebe
            </span>
            <button
              onClick={() => navigate('/app/deals')}
              style={{ fontSize: 12, color: 'var(--brand-primary-mid)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}
            >
              Alle →
            </button>
          </div>

          {loadingLeads ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 4px' }}>
              {[1,2,3,4].map(i => <Skeleton key={i} height={40} />)}
            </div>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 28 }}>📋</div>
              <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>Noch keine Leads vorhanden</div>
              <button onClick={() => navigate('/app/import')} style={{ marginTop: 4, padding: '8px 18px', borderRadius: 8, border: 'none', background: 'var(--brand-primary)', color: 'var(--text-on-brand)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                + Leads importieren
              </button>
            </div>
          ) : (
            leads.map((lead, i) => (
              <div role="button" tabIndex={0} onKeyDown={aufTaste(() => navigate(`/app/betriebe/${lead.id}`))}
                key={lead.id}
                onClick={() => navigate(`/app/betriebe/${lead.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 4px',
                  borderBottom: i < leads.length - 1 ? '1px solid var(--border-light)' : 'none',
                  cursor: 'pointer', transition: 'background 0.1s',
                  borderRadius: 'var(--radius-sm)', gap: 12,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'var(--brand-primary-light)', color: 'var(--brand-primary-mid)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 600, flexShrink: 0,
                }}>
                  {lead.company_name?.[0] || '?'}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {lead.company_name}
                  </div>
                  <div style={{
                    fontSize: 12, color: 'var(--text-tertiary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {lead.city}{lead.city && lead.trade ? ' · ' : ''}{lead.trade}
                  </div>
                </div>

                {lead.analysis_score > 0 && (
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: scoreColor(lead.analysis_score) }}>
                      {lead.analysis_score}
                    </div>
                    <div style={{ width: 40, height: 3, background: 'var(--border-light)', borderRadius: 2, overflow: 'hidden', marginTop: 3 }}>
                      <div style={{ width: `${lead.analysis_score}%`, height: '100%', background: scoreColor(lead.analysis_score), borderRadius: 2 }} />
                    </div>
                  </div>
                )}

                <span style={{ flexShrink: 0 }}>{statusBadge(lead.status)}</span>
              </div>
            ))
          )}
        </Card>

        {/* Letzte Audits */}
        <Card padding="sm" style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)', textTransform: 'uppercase', letterSpacing: '.08em', paddingBottom: 12, borderBottom: '1px solid var(--border-light)', marginBottom: 8 }}>
            Letzte Audits
          </div>

          {loadingSecondary ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px' }}>
              {[1,2,3].map(i => <Skeleton key={i} height={48} />)}
            </div>
          ) : audits.length === 0 ? (
            <EmptyState icon="📊" title="Noch keine Audits" description="Starte dein erstes Website-Audit um Optimierungspotenziale zu entdecken." action={{ label: 'Erstes Audit starten', onClick: () => navigate('/app/audit') }} compact />
          ) : (
            audits.map((audit, i) => {
              const score = audit.total_score || 0;
              const level = score >= 85 ? 'Pt' : score >= 70 ? 'Go' : score >= 50 ? 'Si' : score >= 30 ? 'Br' : 'NC';
              const levelColor =
                score >= 85 ? 'var(--brand-primary)'
                : score >= 70 ? 'var(--status-warning-text)'
                : score >= 50 ? 'var(--text-secondary)'
                : score >= 30 ? '#a06820'
                : 'var(--status-danger-text)';

              return (
                <div key={audit.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px',
                  borderBottom: i < audits.length - 1 ? '1px solid var(--border-light)' : 'none',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 'var(--radius-sm)',
                    background: `color-mix(in srgb, ${levelColor} 9%, transparent)`, color: levelColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>
                    {level}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 500, color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {audit.company_name || audit.website_url}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {datumKurz(audit.created_at)}
                    </div>
                  </div>

                  <div style={{ fontSize: 14, fontWeight: 600, color: levelColor, flexShrink: 0 }}>
                    {score}
                  </div>
                </div>
              );
            })
          )}

          {!loadingSecondary && audits.length > 0 && (
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
              marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-light)',
            }}>
              {[
                { label: 'Ø Score', value: avgScore || '—' },
                { label: 'Audits', value: audits.length },
              ].map(stat => (
                <div key={stat.label} style={{
                  textAlign: 'center', padding: '8px',
                  background: 'var(--bg-app)', borderRadius: 'var(--radius-sm)',
                }}>
                  <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--brand-primary)' }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
